/** Pure presentation logic for BulkTemplateMatchWizard — kept side-effect-free
 *  so it stays vitest-able without jsdom. */
import type { BulkMatchBand, BulkMatchRow } from '../types';
import { BLOCK_NO_QTY } from '../types';

export interface BandTag {
    color: 'green' | 'gold' | 'default';
    label: string;
}

/** Confidence Tag mapping: high=green, medium=gold, none=default. */
export function bandTag(band: BulkMatchBand): BandTag {
    if (band === 'high') return { color: 'green', label: 'High' };
    if (band === 'medium') return { color: 'gold', label: 'Medium' };
    return { color: 'default', label: 'None' };
}

/** Hard blocks cannot be fixed inline; the qty block clears when the user types one. */
export function hardBlocks(row: BulkMatchRow): string[] {
    return row.blocks.filter((b) => b !== BLOCK_NO_QTY);
}

/** Commit-time acceptability (spec §2.2): band ≠ none ∧ no existing mapping ∧
 *  no hard block ∧ positive effective qty (prefilled or user-entered). */
export function isRowAcceptable(row: BulkMatchRow, effectiveQty: number | null): boolean {
    return (
        row.band !== 'none' &&
        !row.existingMapping &&
        hardBlocks(row).length === 0 &&
        effectiveQty !== null &&
        effectiveQty > 0
    );
}

/** Default accept: high only (medium requires a manual tick), unmapped, unblocked,
 *  with a prefilled qty. */
export function defaultAccept(row: BulkMatchRow): boolean {
    return row.band === 'high' && !row.existingMapping && row.blocks.length === 0 && row.qtyPrefill !== null;
}

export interface BulkMatchSummary {
    total: number;
    high: number;
    medium: number;
    none: number;
    alreadyMapped: number;
    accepted: number;
}

export function summarize(rows: BulkMatchRow[], acceptedKeys: ReadonlySet<string>): BulkMatchSummary {
    let high = 0;
    let medium = 0;
    let none = 0;
    let alreadyMapped = 0;
    let accepted = 0;
    for (const r of rows) {
        if (r.band === 'high') high += 1;
        else if (r.band === 'medium') medium += 1;
        else none += 1;
        if (r.existingMapping) alreadyMapped += 1;
        if (acceptedKeys.has(r.taskKey)) accepted += 1;
    }
    return { total: rows.length, high, medium, none, alreadyMapped, accepted };
}
